import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Zap, TrendingUp, DollarSign, Users, CheckCircle, Rocket, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const CapacityLanding = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Helmet>
        <title>Stop Turning Down Work — Scale Your Agency Capacity | HireX8</title>
        <meta name="description" content="Your team is maxed out. A new RFP just landed. HireX8 gets you pre-vetted talent in hours — not weeks — so you never have to turn down a project again." />
        <meta property="og:title" content="Stop Turning Down Work — Scale Your Agency Capacity | HireX8" />
        <meta property="og:description" content="Pre-vetted talent in hours. Zero platform fees. Built for digital agencies." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-orange-500">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/capacity-hero.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/95 via-orange-500/80 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-20">
          <div className="flex flex-col items-start text-left space-y-4 max-w-2xl">
            {/* Logo */}
            <Link to="/" className="mb-4">
              <span className="text-2xl font-bold text-white">HireX8™</span>
            </Link>

            {/* Badge */}
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-white"></div>
              <span className="font-medium text-white/90">Built for Digital Agencies</span>
            </div>

            {/* Main Headline */}
            <p className="text-xl text-white/90">
              Stop turning down work because of:
            </p>

            <div className="space-y-1">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight">
                Capacity
              </h1>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight">
                Speed
              </h1>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight">
                Revenue
              </h1>
            </div>

            {/* Subheadline */}
            <div className="py-2">
              <p className="text-lg text-white/90">
                Your team is maxed out. A new RFP just landed. HireX8 gets you pre-vetted talent in hours — not weeks — so you never have to turn down a project again.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4 pt-4">
              <a
                href="/meet-with-an-expert"
                className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all shadow-lg"
              >
                Talk to an Expert
                <ArrowRight className="w-5 h-5" />
              </a>

            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">The Capacity Problem</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Stop Turning Down <span className="text-orange-500">Work</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every agency hits the wall. Your team is full. A client wants to expand. You need to hire but can't commit to a full-time salary. Here's how agencies solve it.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Capacity */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-orange-500 mb-3">Capacity</h3>
              <p className="text-gray-600">
                Get pre-vetted operators matched to your project in hours. No job posts. No interviews. No onboarding lag. Your team grows when you need it and scales back when you don't.
              </p>
            </div>

            {/* Speed */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-orange-500 mb-3">Speed</h3>
              <p className="text-gray-600">
                Every operator works with AI-assisted workflows by default. That means faster turnarounds, fewer revision cycles, and delivery that keeps clients coming back.
              </p>
            </div>

            {/* Revenue */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-orange-500 mb-3">Revenue</h3>
              <p className="text-gray-600">
                Accept the project. Keep the margin. Zero platform fees means the budget your client approved is the budget that goes to work — not to a marketplace taking its cut.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
              The <span className="text-orange-500">Bandwidth Problem</span> — and How Agencies Fix It
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Problem */}
            <div className="bg-white rounded-2xl p-8 border border-red-200">
              <h3 className="text-xl font-bold text-red-500 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-sm">✗</span>
                The Problem
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-600">
                  <span className="text-red-400 mt-1">•</span>
                  <span>Client wants to expand scope — your team has no bandwidth</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <span className="text-red-400 mt-1">•</span>
                  <span>New RFP lands but you can't staff it in time to respond</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <span className="text-red-400 mt-1">•</span>
                  <span>Hiring takes months, burns cash, and locks in fixed cost</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <span className="text-red-400 mt-1">•</span>
                  <span>Freelancers from marketplaces are unreliable and hard to vet</span>
                </li>
              </ul>
            </div>

            {/* HireX8 */}
            <div className="bg-orange-50 rounded-2xl p-8 border border-orange-200">
              <h3 className="text-xl font-bold text-orange-500 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm">✓</span>
                HireX8
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>Matched with pre-vetted talent in hours — not weeks</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>Say yes to the RFP. Staff it today, deliver on time</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>No hiring, no contracts, no long-term risk — scale per project</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>Every operator is AI-screened and human-verified before you see them</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">Expand Your Offerings</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Access Every <span className="text-orange-500">Skill</span> You Need
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Win work in verticals your current team can't cover. Deploy specialists project by project — no full-time commitment required.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: '🎨', label: 'Design' },
              { icon: '✍️', label: 'Content' },
              { icon: '📈', label: 'SEO' },
              { icon: '💻', label: 'Development' },
              { icon: '📊', label: 'Analytics' },
              { icon: '🎬', label: 'Video' },
              { icon: '📱', label: 'Social Media' },
              { icon: '🤖', label: 'AI Automation' },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 text-center border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all"
              >
                <span className="text-3xl mb-3 block">{service.icon}</span>
                <span className="text-gray-700 font-medium">{service.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">8x</div>
              <p className="text-gray-600">Faster than posting a job and screening candidates yourself</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">0%</div>
              <p className="text-gray-600">Platform fees — your full budget goes to talent and delivery</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">7 min</div>
              <p className="text-gray-600">To match with pre-vetted talent</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">Agency Wins</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              Agencies that said yes — and delivered.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">"We were at full capacity when a $60K project came in. HireX8 had us a senior dev and a UX designer within 24 hours. We took the project. We delivered on time."</p>
              <div>
                <p className="font-bold text-gray-900">Ryan T.</p>
                <p className="text-sm text-gray-500">Founder, 15-person digital agency · Chicago</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">"I used to dread scope expansion conversations because I knew we couldn't staff them. Now I say yes first and sort the team out with HireX8 same day."</p>
              <div>
                <p className="font-bold text-gray-900">Alicia M.</p>
                <p className="text-sm text-gray-500">Project Lead, creative agency · Los Angeles</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">"We went from turning down two out of every five inbound projects to accepting almost everything. Revenue is up 34% this quarter."</p>
              <div>
                <p className="font-bold text-gray-900">Tom B.</p>
                <p className="text-sm text-gray-500">CEO, 22-person agency · London</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-orange-500">
        <div className="max-w-3xl mx-auto text-center">
          <Rocket className="w-12 h-12 text-white mx-auto mb-6" />

          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            The next project doesn't have to be a no.
          </h2>

          <p className="text-xl text-white/90 mb-10">
            Talk to one of our agency specialists. Tell us what's coming up and we'll show you exactly how fast we can staff it — and what it'll cost you.
          </p>

          <a
            href="/meet-with-an-expert"
            className="inline-flex items-center gap-3 bg-black hover:bg-gray-900 text-white text-xl font-bold px-12 py-5 rounded-lg shadow-lg transition-all hover:scale-105"
          >
            Book a Free 20-Min Call
            <ArrowRight className="w-6 h-6" />
          </a>

          <p className="mt-6 text-white/70 text-sm">
            No commitment. No sales pressure. Just straight answers on whether HireX8 fits your agency.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-2xl font-bold text-white">
            HireX8™
          </Link>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <span>© 2026 HireX8. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CapacityLanding;
